<template>
  <v-card height="100%" flat color="bg1" class="composite-source-list">
    <!-- Header -->
    <v-list-item
      two-line
      :class="$vuetify.theme.isDark ? 'bg2' : 'white'"
      style="max-height: 65px">
      <v-list-item-content>
        <v-list-item-title class="title">Composite Sources</v-list-item-title>
        <v-list-item-subtitle class="subtitle-1">
          Join your starting data to the records around it and publish it as a reusable source
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>

    <v-divider></v-divider>

    <v-sheet color="transparent" class="pa-4 d-flex flex-column" style="height: calc(100% - 80px)">
      <!-- Target picker -->
      <v-sheet outlined class="pa-4 rounded-lg mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="primary" class="mr-2">mdi-target</v-icon>
          <span class="title">Target</span>
        </div>

        <v-autocomplete
          attach
          v-model="selectedTargetId"
          :items="targetItems"
          :loading="loadingTargets"
          item-text="name"
          item-value="id"
          label="Select target"
          placeholder="Choose a facility"
          outlined
          dense
          hide-details="auto"
          clearable>
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.disabled" class="composite-source-list__muted">
                {{ copy.GroupTargetDisabledNote }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <v-alert
          v-if="showTargetLoadError"
          type="error"
          outlined
          dense
          class="mt-3 mb-0"
          role="alert">
          <div class="composite-source-list__target-error">
            <span>
              Targets could not be loaded. Existing target choices are unchanged.
            </span>
            <v-btn text small color="error" :loading="loadingTargets" @click="fetchTargets">
              Retry
            </v-btn>
          </div>
        </v-alert>
      </v-sheet>

      <!-- Sources. Same chrome as the Target card above so the two read as siblings. -->
      <v-sheet outlined class="pa-4 rounded-lg">
        <div
          v-if="(targetLoadState === 'idle' || targetLoadState === 'loading') && !targets.length"
          class="ds-empty-state"
          role="status"
          aria-live="polite">
          <envizion-loader :size="48" />
          <div class="ds-empty-state__title">Loading targets</div>
          <div class="ds-empty-state__description composite-source-list__muted">
            Finding the facilities available to you…
          </div>
        </div>

        <div v-else-if="showTargetLoadError && !targets.length" class="ds-empty-state" role="alert">
          <v-icon class="ds-empty-state__icon" color="error">mdi-cloud-alert-outline</v-icon>
          <div class="ds-empty-state__title">Targets unavailable</div>
          <div class="ds-empty-state__description composite-source-list__muted">
            Retry loading targets above. This is a connection problem, not an access result.
          </div>
        </div>

        <!-- No accessible target at all -->
        <div v-else-if="showNoTargetsState" class="ds-empty-state">
          <v-icon class="ds-empty-state__icon">mdi-domain-off</v-icon>
          <div class="ds-empty-state__title">{{ copy.NoTargetsTitle }}</div>
          <div class="ds-empty-state__description composite-source-list__muted">
            {{ copy.NoTargetsMessage }}
          </div>
        </div>

        <!-- Only group targets are reachable: nothing here is usable yet -->
        <div v-else-if="showGroupOnlyState" class="ds-empty-state">
          <v-icon class="ds-empty-state__icon">mdi-account-group-outline</v-icon>
          <div class="ds-empty-state__title">{{ copy.GroupOnlyTargetsTitle }}</div>
          <div class="ds-empty-state__description composite-source-list__muted">
            {{ copy.GroupOnlyTargetsMessage }}
          </div>
        </div>

        <!-- Target chosen: its definitions -->
        <template v-else-if="selectedTarget">
          <div class="ds-section-header">
            <div class="ds-section-header__content">
              <p class="ds-section-header__title">Sources for {{ selectedTarget.name }}</p>
              <p class="ds-section-header__subtitle composite-source-list__muted">
                {{ definitionCountLabel }}
              </p>
            </div>
            <v-btn v-if="canCreateSource" color="primary" depressed small @click="openCreate">
              <v-icon left size="18">mdi-plus</v-icon>
              Create source
            </v-btn>
          </div>

          <v-alert
            v-if="showDefinitionsLoadError"
            type="error"
            outlined
            dense
            class="mb-3"
            role="alert">
            <div class="composite-source-list__target-error">
              <span>
                {{ definitionsLoadErrorMessage }}
              </span>
              <v-btn
                text
                small
                color="error"
                :loading="loadingDefinitions"
                @click="fetchDefinitions">
                Retry
              </v-btn>
            </div>
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="definitions"
            :loading="loadingDefinitions"
            loading-text="Loading composite sources..."
            disable-pagination
            hide-default-footer
            class="composite-source-list__table">
            <template v-slot:[`item.scope`]="{ item }">
              <span>{{ scopeLabel(item) }}</span>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip x-small :color="statusColor(item.status)" dark>
                {{ statusText(item.status) }}
              </v-chip>
            </template>

            <template v-slot:[`item.updateDate`]="{ item }">
              <span>{{ formatUpdated(item) }}</span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <div class="composite-source-list__actions">
                <v-tooltip v-if="canEditSource" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      small
                      :aria-label="`Edit ${item.name}`"
                      :loading="loadingEditId === item.id"
                      :disabled="!!deletingDefinitionId"
                      @click="openEdit(item)">
                      <v-icon size="18">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit {{ item.name }}</span>
                </v-tooltip>

                <v-tooltip v-if="canDeleteSource" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      small
                      color="error"
                      :aria-label="`Delete ${item.name}`"
                      :loading="deletingDefinitionId === item.id"
                      :disabled="!!deletingDefinitionId || loadingEditId === item.id"
                      @click="requestDelete(item)">
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete {{ item.name }}</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:no-data>
              <div v-if="showNoDefinitionsState" class="ds-empty-state">
                <v-icon class="ds-empty-state__icon">mdi-table-large</v-icon>
                <div class="ds-empty-state__title">{{ copy.NoDefinitionsTitle }}</div>
                <div class="ds-empty-state__description composite-source-list__muted">
                  {{ copy.NoDefinitionsMessage }}
                </div>
              </div>
              <div
                v-else-if="definitionsLoadState === 'error'"
                class="ds-empty-state"
                role="alert">
                <v-icon class="ds-empty-state__icon" color="error">mdi-cloud-alert-outline</v-icon>
                <div class="ds-empty-state__title">Source list unavailable</div>
                <div class="ds-empty-state__description composite-source-list__muted">
                  Retry loading composite sources above. This is not an empty result.
                </div>
              </div>
            </template>
          </v-data-table>
        </template>

        <!-- Targets exist, none chosen -->
        <div v-else class="ds-empty-state">
          <v-icon class="ds-empty-state__icon">mdi-cursor-default-click-outline</v-icon>
          <div class="ds-empty-state__title">{{ copy.NoTargetSelectedTitle }}</div>
          <div class="ds-empty-state__description composite-source-list__muted">
            {{ copy.NoTargetSelectedMessage }}
          </div>
        </div>
      </v-sheet>
    </v-sheet>

    <composite-source-wizard
      v-if="wizardOpen"
      v-model="wizardOpen"
      :target="selectedTarget"
      :definition="editingDefinition"
      @saved="onWizardSaved"
      @close="onWizardClosed" />

    <v-dialog
      v-model="deleteConfirmOpen"
      max-width="480"
      persistent
      aria-labelledby="composite-source-delete-title">
      <v-card>
        <v-card-title
          id="composite-source-delete-title"
          class="composite-source-list__dialog-title">
          Delete “{{ pendingDeleteName }}”?
        </v-card-title>
        <v-card-text>
          This permanently removes the composite source and its compiled view together. Saved
          dashboard widgets are not rewritten and may stop working.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="!!deletingDefinitionId" @click="cancelDelete">Keep source</v-btn>
          <v-btn
            color="error"
            depressed
            :loading="!!deletingDefinitionId"
            @click="deleteDefinition">
            Delete source
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  import compositeSourceService from '../service/compositeSourceService';
  import {
    CompositeSourceTargetKind,
    CompositeSourceCopy,
    getCompositeSourceStatusColor,
    getCompositeSourceStatusText,
  } from '../config/compositeSourceEnums';
  import { createLatestRequestGate } from '../model/authoringWorkflow';
  import {
    CompositeSourceConflict,
    conflictCodeOf,
    describeApiError,
  } from '../components/wizardErrors';
  import { $formatDate, $has } from '@Utils';
  import { log, error } from '@/utils/debugLogger';
  import permissions from '@/features/Share/config/permissions';
  import CompositeSourceWizard from '../components/CompositeSourceWizard.vue';

  export default {
    name: 'CompositeSourceList',

    components: { CompositeSourceWizard },

    data() {
      return {
        wizardOpen: false,
        editingDefinition: null,
        targets: [],
        selectedTargetId: null,
        loadingTargets: false,
        targetLoadState: 'idle',
        definitions: [],
        definitionsTargetId: null,
        loadingDefinitions: false,
        definitionsLoadState: 'idle',
        definitionsRequestId: 0,
        editRequestGate: createLatestRequestGate(),
        loadingEditId: null,
        deleteConfirmOpen: false,
        pendingDeleteDefinition: null,
        pendingDeleteFacilityId: null,
        deletingDefinitionId: null,
        copy: CompositeSourceCopy,
        headers: [
          { text: 'Name', value: 'name' },
          { text: 'Scope', value: 'scope', sortable: false, width: '140px' },
          { text: 'Status', value: 'status', sortable: false, width: '120px' },
          { text: 'Updated', value: 'updateDate', width: '180px' },
          { text: '', value: 'actions', sortable: false, align: 'end', width: '104px' },
        ],
      };
    },

    computed: {
      canCreateSource() {
        return this.hasPermission(permissions.DataSources.Create);
      },

      canEditSource() {
        return this.hasPermission(permissions.DataSources.Update);
      },

      canDeleteSource() {
        return this.hasPermission(permissions.DataSources.Delete);
      },

      facilityTargets() {
        return this.targets.filter((t) => t.kind === CompositeSourceTargetKind.Facility);
      },

      groupTargets() {
        return this.targets.filter((t) => t.kind === CompositeSourceTargetKind.FacilityGroup);
      },

      /**
       * Facilities first, then the groups those facilities belong to. Group rows are rendered
       * so a curator can see the scope exists, and disabled because the engine refuses it:
       * the M1 validator answers UnknownScopeKind for anything but `facility`.
       */
      targetItems() {
        const items = [];
        if (this.facilityTargets.length) {
          items.push({ header: 'Facilities' });
          items.push(...this.facilityTargets);
        }
        if (this.groupTargets.length) {
          items.push({ header: 'Facility groups' });
          items.push(...this.groupTargets);
        }
        return items;
      },

      selectedTarget() {
        if (!this.selectedTargetId) return null;
        return this.facilityTargets.find((t) => t.id === this.selectedTargetId) || null;
      },

      showNoTargetsState() {
        return this.targetLoadState === 'ready' && !this.targets.length;
      },

      showGroupOnlyState() {
        return this.targetLoadState === 'ready'
          && !this.facilityTargets.length
          && this.groupTargets.length > 0;
      },

      showTargetLoadError() {
        return this.targetLoadState === 'error';
      },

      showDefinitionsLoadError() {
        return this.definitionsLoadState === 'error';
      },

      showNoDefinitionsState() {
        return this.definitionsLoadState === 'ready' && !this.definitions.length;
      },

      definitionCountLabel() {
        if (this.loadingDefinitions) return 'Loading...';
        if (this.definitionsLoadState === 'error' && !this.definitions.length) return 'Unavailable';
        const count = this.definitions.length;
        const countLabel = count === 1 ? '1 Source' : `${count} Sources`;
        return this.definitionsLoadState === 'error'
          ? `${countLabel} shown · refresh failed`
          : countLabel;
      },

      definitionsLoadErrorMessage() {
        return this.definitions.length
          ? 'Composite sources could not be refreshed. Existing rows are still shown.'
          : 'Composite sources could not be loaded for this target. Retry to check its current sources.';
      },

      pendingDeleteName() {
        return (this.pendingDeleteDefinition && this.pendingDeleteDefinition.name) || 'this source';
      },
    },

    watch: {
      selectedTargetId(value) {
        log('[CompositeSourceList] target changed', { targetId: value });
        this.editRequestGate.invalidate();
        this.loadingEditId = null;
        if (!this.deletingDefinitionId) this.cancelDelete();
        this.fetchDefinitions();
      },
    },

    created() {
      this.fetchTargets();
    },

    methods: {
      statusColor: getCompositeSourceStatusColor,
      statusText: getCompositeSourceStatusText,

      hasPermission(permission) {
        const check = typeof this.$has === 'function' ? this.$has : $has;
        return check(permission);
      },

      /**
       * Targets come from the accessible-facility list the app already caches for 5 minutes
       * (FACILITY/FETCH_ACCESSIBLE). Each entry carries its own `facilityGroup`, so the group
       * targets are derived from the same payload — no second endpoint, and the only groups
       * offered are ones the curator's own facilities belong to.
       */
      async fetchTargets() {
        this.loadingTargets = true;
        this.targetLoadState = 'loading';
        try {
          const facilities = await this.$store.dispatch('FACILITY/FETCH_ACCESSIBLE');
          const list = Array.isArray(facilities) ? facilities : [];

          const facilityTargets = list.map((f) => ({
            kind: CompositeSourceTargetKind.Facility,
            id: f.id,
            name: f.name || 'Unnamed facility',
          }));

          const groupsById = new Map();
          list.forEach((f) => {
            const group = f.facilityGroup;
            if (group && group.id && !groupsById.has(group.id)) {
              groupsById.set(group.id, {
                kind: CompositeSourceTargetKind.FacilityGroup,
                id: group.id,
                name: group.name || 'Unnamed group',
                disabled: true,
              });
            }
          });

          this.targets = [...facilityTargets, ...groupsById.values()];
          this.targetLoadState = 'ready';
          log('[CompositeSourceList] targets loaded', {
            facilities: facilityTargets.length,
            groups: groupsById.size,
          });
        } catch (err) {
          error('[CompositeSourceList] failed to load targets', err);
          this.targetLoadState = 'error';
        } finally {
          this.loadingTargets = false;
        }
      },

      /**
       * Every fetch takes a sequence number and only the newest one is allowed to write.
       * Switching target quickly can land responses out of order, and a late loser must
       * neither overwrite the newer rows nor clear the spinner the newer request is still
       * showing — hence the guard covers the loading flag too, not just the data.
       */
      async fetchDefinitions() {
        if (!this.selectedTarget) {
          this.definitionsRequestId += 1;
          this.definitions = [];
          this.definitionsTargetId = null;
          this.loadingDefinitions = false;
          this.definitionsLoadState = 'idle';
          return;
        }

        const facilityId = this.selectedTarget.id;
        const requestId = ++this.definitionsRequestId;
        if (this.definitionsTargetId !== facilityId) {
          this.definitions = [];
          this.definitionsTargetId = facilityId;
        }
        this.loadingDefinitions = true;
        this.definitionsLoadState = 'loading';

        try {
          const res = await compositeSourceService.list(facilityId, { itemsPerPage: -1 });
          if (requestId !== this.definitionsRequestId) {
            log('[CompositeSourceList] stale definitions response ignored', { facilityId });
            return;
          }
          this.definitions = (res && res.data && res.data.items) || [];
          this.definitionsLoadState = 'ready';
          log('[CompositeSourceList] definitions loaded', {
            facilityId,
            count: this.definitions.length,
          });
        } catch (err) {
          if (requestId !== this.definitionsRequestId) return;
          error('[CompositeSourceList] failed to load definitions', { facilityId, err });
          this.definitionsLoadState = 'error';
        } finally {
          if (requestId === this.definitionsRequestId) this.loadingDefinitions = false;
        }
      },

      /**
       * A definition listed under a facility target may be owned by that facility's GROUP —
       * the server scopes the list to facility OR its group — so the row reports its own
       * scope rather than echoing the selected target.
       */
      scopeLabel(item) {
        return item && item.facilityGroupId ? 'Group' : 'Facility';
      },

      formatUpdated(item) {
        const value = (item && (item.updateDate || item.createDate)) || null;
        return value ? $formatDate(value) : '—';
      },

      openCreate() {
        if (!this.hasPermission(permissions.DataSources.Create)) return;
        log('[CompositeSourceList] create requested', { targetId: this.selectedTargetId });
        this.editRequestGate.invalidate();
        this.loadingEditId = null;
        this.editingDefinition = null;
        this.wizardOpen = true;
      },

      /**
       * Always re-reads the row. A list response can be old by the time Edit is clicked, and its
       * rowVersion must never seed an authoring session that starts stale. The generation gate
       * also means rapid A -> B clicks cannot let A's later response replace B.
       */
      async openEdit(item) {
        if (!this.hasPermission(permissions.DataSources.Update)) return;
        if (!item || !item.id || !this.selectedTarget) return;

        const facilityId = this.selectedTarget.id;
        const requestGeneration = this.editRequestGate.next();
        this.loadingEditId = item.id;
        log('[CompositeSourceList] edit requested', { definitionId: item && item.id });

        try {
          const res = await compositeSourceService.get(item.id, facilityId);
          if (!this.editRequestGate.isCurrent(requestGeneration)) return;

          this.editingDefinition = (res && res.data) || null;
          this.wizardOpen = !!this.editingDefinition;
        } catch (err) {
          if (!this.editRequestGate.isCurrent(requestGeneration)) return;
          error('[CompositeSourceList] failed to open definition', { definitionId: item.id, err });
          this.$dialog.notify.error(describeApiError(err, 'Failed to open this composite source'), {
            position: 'top-right',
            timeout: 5000,
          });
        } finally {
          if (this.editRequestGate.isCurrent(requestGeneration)) this.loadingEditId = null;
        }
      },

      requestDelete(item) {
        if (!this.hasPermission(permissions.DataSources.Delete)) return;
        if (!item || !item.id || this.deletingDefinitionId) return;
        this.pendingDeleteDefinition = item;
        this.pendingDeleteFacilityId = this.selectedTarget && this.selectedTarget.id;
        this.deleteConfirmOpen = true;
      },

      cancelDelete() {
        if (this.deletingDefinitionId) return;
        this.deleteConfirmOpen = false;
        this.pendingDeleteDefinition = null;
        this.pendingDeleteFacilityId = null;
      },

      async deleteDefinition() {
        if (!this.hasPermission(permissions.DataSources.Delete)) return;
        const definition = this.pendingDeleteDefinition;
        const facilityId = this.pendingDeleteFacilityId;
        if (!definition || !facilityId || this.deletingDefinitionId) return;

        this.deletingDefinitionId = definition.id;
        try {
          await compositeSourceService.delete(
            definition.id,
            facilityId,
            definition.rowVersion,
          );
          this.deleteConfirmOpen = false;
          this.pendingDeleteDefinition = null;
          this.pendingDeleteFacilityId = null;
          this.$dialog.notify.success('Composite source and compiled view deleted', {
            position: 'top-right',
            timeout: 3500,
          });
          await this.fetchDefinitions();
        } catch (err) {
          error('[CompositeSourceList] failed to delete definition', {
            definitionId: definition.id,
            err,
          });

          const rowChanged = conflictCodeOf(err) === CompositeSourceConflict.RowVersionMismatch;
          let deleteMessage = describeApiError(err, 'Failed to delete this composite source');

          if (rowChanged) {
            this.deleteConfirmOpen = false;
            this.pendingDeleteDefinition = null;
            this.pendingDeleteFacilityId = null;

            let refreshed = false;
            try {
              const freshResponse = await compositeSourceService.get(definition.id, facilityId);
              const freshDefinition = freshResponse && freshResponse.data;
              const rowIndex = this.definitions.findIndex((item) => item.id === definition.id);
              if (freshDefinition && this.definitionsTargetId === facilityId && rowIndex >= 0) {
                this.definitions.splice(rowIndex, 1, freshDefinition);
                refreshed = true;
              }
            } catch (refreshErr) {
              error('[CompositeSourceList] failed to refresh definition after delete conflict', {
                definitionId: definition.id,
                refreshErr,
              });
            }

            deleteMessage = refreshed
              ? 'This source changed since you opened the delete confirmation. Nothing was deleted. The latest version was refreshed; review it before trying again.'
              : 'This source changed since you opened the delete confirmation. Nothing was deleted. Reload the source list before trying again.';
          }

          this.$dialog.notify.error(deleteMessage, {
            position: 'top-right',
            timeout: 6000,
          });
        } finally {
          this.deletingDefinitionId = null;
        }
      },

      /**
       * Refetches rather than patching the row in place: a save changes the name, the status and
       * the rowVersion at once, and re-reading is what keeps the next edit's If-Match current.
       */
      onWizardSaved() {
        this.fetchDefinitions();
      },

      onWizardClosed() {
        this.wizardOpen = false;
        this.editingDefinition = null;
      },
    },
  };
</script>

<style scoped>
  /*
   * The .ds-* description/subtitle rules resolve to --v-text_secondary-base (#C8C8C8), which
   * is a light-grey intended for dark surfaces. This page renders on white/bg1, so it is
   * overridden with a muted tone that actually meets contrast there.
   */
  .composite-source-list__muted {
    color: #5c6b66;
  }

  .theme--dark .composite-source-list__muted {
    color: var(--v-text_secondary-base);
  }

  .composite-source-list__table {
    background-color: transparent;
  }

  .composite-source-list__actions {
    display: flex;
    justify-content: flex-end;
  }

  .composite-source-list__dialog-title {
    font-family: var(--ds-font-family-heading);
    font-size: var(--ds-font-title);
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  .composite-source-list__target-error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ds-space-sm);
  }
</style>
